/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { MapPin, X, Navigation } from 'lucide-react';
import { useMunicipalities } from './MunicipalitiesContext';
import Modal from './Modal';
import { filterByPrefix, selectStyles, selectClassNames } from '../utils/selectStyles';

export default function NearestLocationModal({ isOpen, initialCity = '', onConfirm, onClose }) {
  const [city, setCity] = useState(initialCity);

  const { names } = useMunicipalities();
  const gradoviOptions = useMemo(() => names.map((g) => ({ value: g, label: g })), [names]);

  useEffect(() => {
    if (isOpen) setCity(initialCity);
  }, [isOpen, initialCity]);

  const handleConfirm = () => {
    if (!city) return;
    onConfirm(city);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      label="Najbliži oglasi"
      panelClassName="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
    >
            <div className="flex items-center justify-between px-6 py-5 border-b border-sage-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sage-50 flex items-center justify-center text-sage-600">
                  <Navigation size={18} />
                </div>
                <h3 className="text-lg font-serif font-bold text-earth-900">Najbliži oglasi</h3>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl hover:bg-sage-50 flex items-center justify-center text-sage-500 hover:text-sage-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-6">
              <p className="text-sm text-sage-600 mb-4 leading-relaxed">
                Izaberite vašu opštinu kako bismo prikazali oglase poređane po blizini.
              </p>

              <label className="block text-sm font-bold text-earth-900 mb-2 ml-1">Opština</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 z-10" size={18} />
                <Select
                  options={gradoviOptions}
                  value={gradoviOptions.find((o) => o.value === city) || null}
                  onChange={(opt) => setCity(opt?.value || '')}
                  placeholder="Izaberite opštinu (kucajte za pretragu)"
                  isSearchable
                  isClearable
                  filterOption={filterByPrefix}
                  noOptionsMessage={() => 'Nema rezultata'}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  styles={selectStyles}
                  unstyled
                  classNames={selectClassNames}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-sage-50 flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-2xl text-sm font-bold text-sage-700 hover:bg-sage-50 transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleConfirm}
                disabled={!city}
                className="px-5 py-2.5 rounded-2xl text-sm font-bold bg-sage-600 text-white hover:bg-sage-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Primeni
              </button>
            </div>
    </Modal>
  );
}
